import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useQueueStore } from "@/lib/store";
import { MasterPatient, Consultation, Patient, Prescription, Bill } from "@/types";
import { 
  User, 
  Calendar, 
  Phone, 
  MapPin, 
  FileText, 
  Stethoscope, 
  Receipt,
  Clock,
  CheckCircle,
  AlertCircle,
  UserCheck,
  TrendingUp
} from "lucide-react";
import { formatPatientId } from '@/lib/utils/patientId';
import { safeFormatDateOnly, safeFormatDateTime } from '@/lib/utils/dateUtils';

interface PatientHistoryDialogProps {
  masterPatient: MasterPatient | null;
  isOpen: boolean;
  onClose: () => void;
}

export function PatientHistoryDialog({ masterPatient, isOpen, onClose }: PatientHistoryDialogProps) {
  const { 
    consultations = [], 
    fetchConsultations,
    patients = [],
    prescriptions = [],
    fetchPrescriptions,
    bills = [],
    fetchBills
  } = useQueueStore();

  const [activeTab, setActiveTab] = useState("overview");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && masterPatient) {
      setIsLoading(true);
      Promise.all([
        fetchConsultations(),
        fetchPrescriptions(),
        fetchBills()
      ]).finally(() => setIsLoading(false));
    }
  }, [isOpen, masterPatient, fetchConsultations, fetchPrescriptions, fetchBills]);

  if (!masterPatient) return null;

  // Get all consultations for this master patient
  const patientConsultations = consultations.filter(c => c.masterPatientId === masterPatient.id);
  
  // Get all patient records for this master patient
  // Check multiple ways patients can be linked:
  // 1. Through consultations (patientId in consultations)
  // 2. Direct masterPatientId link on patient record
  // 3. By contact number (same contact = same person)
  const patientRecords = patients.filter(p => {
    // Check if patient is linked through consultations
    const linkedViaConsultation = patientConsultations.some(c => c.patientId === p.id);
    
    // Check if patient has direct masterPatientId link
    const linkedViaMasterId = p.masterPatientId === masterPatient.id;
    
    // Check if patient has same contact number (same person, different visits)
    const linkedViaContact = p.contactNumber === masterPatient.contactNumber;
    
    return linkedViaConsultation || linkedViaMasterId || linkedViaContact;
  });
  
  // Get all prescriptions for this patient (using all patient IDs)
  // Collect all patient IDs that could be linked to this master patient
  const allLinkedPatientIds = new Set<string>();
  
  // Add patient IDs from patientRecords
  patientRecords.forEach(pr => allLinkedPatientIds.add(pr.id));
  
  // Also add ALL patients with matching contact number (same person, different visits)
  // This ensures we catch all prescriptions even if patientRecords filtering missed some
  patients.forEach(p => {
    if (p.contactNumber === masterPatient.contactNumber) {
      allLinkedPatientIds.add(p.id);
    }
  });
  
  // Also check consultations for additional patient IDs
  patientConsultations.forEach(c => {
    if (c.patientId) {
      allLinkedPatientIds.add(c.patientId);
    }
  });
  
  // Get prescriptions for all linked patient IDs
  // Handle both patientId and patient_id formats
  const patientPrescriptions = prescriptions.filter(p => {
    const prescriptionPatientId = p.patientId || (p as any).patient_id;
    return allLinkedPatientIds.has(prescriptionPatientId);
  });
  
  // Debug logging (can be removed later)
  console.log('Patient History Debug:', {
    masterPatientId: masterPatient.id,
    masterPatientContact: masterPatient.contactNumber,
    patientRecordsCount: patientRecords.length,
    patientRecordsIds: patientRecords.map(p => p.id),
    allLinkedPatientIds: Array.from(allLinkedPatientIds),
    totalPrescriptions: prescriptions.length,
    matchedPrescriptions: patientPrescriptions.length,
    matchedPrescriptionIds: patientPrescriptions.map(p => p.id)
  });
  
  // Get all bills for this patient (using all patient IDs)
  const patientBills = bills.filter(b => 
    patientRecords.some(pr => pr.id === b.patientId)
  );

  // Calculate statistics
  // Total visits = consultations OR patient records (if no consultations exist, count patient records)
  const totalVisits = patientConsultations.length > 0 
    ? patientConsultations.length 
    : patientRecords.length;
  
  // Completed visits = completed consultations OR completed patient records
  const completedVisits = patientConsultations.length > 0
    ? patientConsultations.filter(c => c.status === 'completed').length
    : patientRecords.filter(p => p.status === 'completed').length;
  
  // Calculate total revenue from bills
  const totalRevenue = patientBills.reduce((sum, bill) => sum + (bill.totalAmount || 0), 0);
  
  // Also calculate from patient payment history if bills are not available
  const revenueFromPayments = patientRecords.reduce((sum, patient) => {
    const paymentHistoryTotal = (patient.paymentHistory || []).reduce((pSum: number, payment: any) => 
      pSum + (Number(payment.amount) || 0), 0
    );
    return sum + paymentHistoryTotal;
  }, 0);
  
  const totalRevenueCombined = totalRevenue > 0 ? totalRevenue : revenueFromPayments;
  const avgRevenuePerVisit = totalVisits > 0 ? totalRevenueCombined / totalVisits : 0;
  
  // Get last visit date from consultations or patient records
  const lastVisit = patientConsultations.length > 0 
    ? new Date(Math.max(...patientConsultations.map(c => new Date(c.registeredAt).getTime())))
    : patientRecords.length > 0
    ? new Date(Math.max(...patientRecords.map(p => new Date(p.registeredAt).getTime())))
    : null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in-consultation': return 'bg-blue-100 text-blue-800';
      case 'waiting': return 'bg-yellow-100 text-yellow-800';
      case 'no-show': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'emergency': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Patient History: {masterPatient.name}
          </DialogTitle>
          <DialogDescription>
            View consultation history, prescriptions, and medical records for this patient.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hospital-primary"></div>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger 
                value="overview" 
                className="text-xs sm:text-sm transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-700 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=active]:scale-105 data-[state=active]:ring-2 data-[state=active]:ring-blue-400/50 data-[state=inactive]:hover:bg-gray-200/50"
              >
                Overview
              </TabsTrigger>
              <TabsTrigger 
                value="visits" 
                className="text-xs sm:text-sm transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-700 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=active]:scale-105 data-[state=active]:ring-2 data-[state=active]:ring-blue-400/50 data-[state=inactive]:hover:bg-gray-200/50"
              >
                Visit History
              </TabsTrigger>
              <TabsTrigger 
                value="prescriptions" 
                className="text-xs sm:text-sm transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-700 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=active]:scale-105 data-[state=active]:ring-2 data-[state=active]:ring-blue-400/50 data-[state=inactive]:hover:bg-gray-200/50"
              >
                Prescriptions
              </TabsTrigger>
              <TabsTrigger 
                value="bills" 
                className="text-xs sm:text-sm transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-700 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=active]:scale-105 data-[state=active]:ring-2 data-[state=active]:ring-blue-400/50 data-[state=inactive]:hover:bg-gray-200/50"
              >
                Bills & Payments
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Patient Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Patient Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">Name:</span>
                        <span>{masterPatient.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">Age:</span>
                        <span>{masterPatient.age}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <UserCheck className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">Gender:</span>
                        <span className="capitalize">{masterPatient.gender}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">Contact:</span>
                        <span>{masterPatient.contactNumber}</span>
                      </div>
                      {masterPatient.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-gray-500" />
                          <span className="font-medium">Location:</span>
                          <span>{masterPatient.location}</span>
                        </div>
                      )}
                      {masterPatient.referralDoctor && (
                        <div className="flex items-center gap-2">
                          <Stethoscope className="h-4 w-4 text-gray-500" />
                          <span className="font-medium">Referral Doctor:</span>
                          <span>{masterPatient.referralDoctor}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Medical Information */}
                  {(masterPatient.bloodGroup || masterPatient.allergies || masterPatient.medicalHistory) && (
                    <div className="mt-6 pt-4 border-t">
                      <h4 className="font-medium mb-3">Medical Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {masterPatient.bloodGroup && (
                          <div>
                            <span className="text-sm font-medium text-gray-600">Blood Group:</span>
                            <p className="text-sm">{masterPatient.bloodGroup}</p>
                          </div>
                        )}
                        {masterPatient.allergies && (
                          <div>
                            <span className="text-sm font-medium text-gray-600">Allergies:</span>
                            <p className="text-sm">{masterPatient.allergies}</p>
                          </div>
                        )}
                        {masterPatient.medicalHistory && (
                          <div>
                            <span className="text-sm font-medium text-gray-600">Medical History:</span>
                            <p className="text-sm">{masterPatient.medicalHistory}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-600">Total Visits</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">{totalVisits}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-gray-600">Completed</span>
                    </div>
                    <p className="text-2xl font-bold text-green-600">{completedVisits}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-purple-600" />
                      <span className="text-sm font-medium text-gray-600">Total Revenue</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-600">₹{totalRevenueCombined.toFixed(2)}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-4 w-4 text-orange-600" />
                      <span className="text-sm font-medium text-gray-600">Avg/Visit</span>
                    </div>
                    <p className="text-2xl font-bold text-orange-600">₹{avgRevenuePerVisit.toFixed(2)}</p>
                  </CardContent>
                </Card>
              </div>

            </TabsContent>

            {/* Visit History Tab */}
            <TabsContent value="visits" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Complete Visit History</CardTitle>
                </CardHeader>
                <CardContent>
                  {patientConsultations.length === 0 && patientRecords.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No visits recorded yet.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[120px] text-center">Date</TableHead>
                          <TableHead className="w-[100px] text-center">Status</TableHead>
                          <TableHead className="w-[100px] text-center">Priority</TableHead>
                          <TableHead className="w-[200px] text-center">Reason for Visit</TableHead>
                          <TableHead className="w-[200px] text-center">Clinical Diagnosis</TableHead>
                          <TableHead className="text-center">Notes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {/* Show consultations if available */}
                        {patientConsultations.length > 0 ? (
                          patientConsultations.map((consultation) => {
                            const patient = patientRecords.find(p => p.id === consultation.patientId);
                            // Find prescription for this patient visit - match by patientId and date proximity
                            const consultationDate = new Date(consultation.registeredAt);
                            const prescriptionsForPatient = patientPrescriptions.filter(pr => pr.patientId === consultation.patientId);
                            
                            // Try to find prescription on the same day, otherwise get the most recent one
                            let prescription = prescriptionsForPatient.find(pr => {
                              const prDate = new Date(pr.createdAt);
                              return prDate.toDateString() === consultationDate.toDateString();
                            });
                            
                            // If no same-day match, get the most recent prescription for this patient
                            if (!prescription && prescriptionsForPatient.length > 0) {
                              prescription = prescriptionsForPatient.sort((a, b) => 
                                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                              )[0];
                            }
                            
                            return (
                              <TableRow key={consultation.id}>
                                <TableCell className="text-center whitespace-nowrap">
                                  <div className="flex items-center justify-center gap-2">
                                    <Calendar className="h-4 w-4 text-gray-500" />
                                    <span className="text-sm">
                                      {safeFormatDateOnly(consultation.registeredAt)}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge className={`${getStatusColor(consultation.status)} text-xs`}>
                                    {consultation.status}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge className={`${getPriorityColor(consultation.priority)} text-xs`}>
                                    {consultation.priority}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <span className="text-sm">
                                    {consultation.reason || 'No reason provided'}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center">
                                  <span className="text-sm font-medium">
                                    {prescription?.diagnosis && prescription.diagnosis.trim() 
                                      ? prescription.diagnosis 
                                      : '-'}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center">
                                  <span className="text-sm">
                                    {consultation.notes || 'No notes recorded'}
                                  </span>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          // If no consultations, show patient records directly
                          patientRecords.map((patient) => {
                            // Find prescription for this patient - get the most recent one
                            const prescriptionsForPatient = patientPrescriptions.filter(pr => pr.patientId === patient.id);
                            const prescription = prescriptionsForPatient.length > 0
                              ? prescriptionsForPatient.sort((a, b) => 
                                  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                                )[0]
                              : null;
                            return (
                              <TableRow key={patient.id}>
                                <TableCell className="text-center whitespace-nowrap">
                                  <div className="flex items-center justify-center gap-2">
                                    <Calendar className="h-4 w-4 text-gray-500" />
                                    <span className="text-sm">
                                      {safeFormatDateOnly(patient.registeredAt)}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge className={`${getStatusColor(patient.status)} text-xs`}>
                                    {patient.status}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge className={`${getPriorityColor(patient.priority)} text-xs`}>
                                    {patient.priority}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <span className="text-sm">
                                    {patient.reason || 'No reason provided'}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center">
                                  <span className="text-sm font-medium">
                                    {prescription?.diagnosis && prescription.diagnosis.trim() 
                                      ? prescription.diagnosis 
                                      : '-'}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center">
                                  <span className="text-sm">
                                    {patient.notes || 'No notes recorded'}
                                  </span>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Prescriptions Tab */}
            <TabsContent value="prescriptions" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Prescription History</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {patientPrescriptions.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No prescriptions found.</p>
                    ) : (
                      patientPrescriptions.map((prescription) => (
                        <div key={prescription.id} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-gray-500" />
                              <span className="font-medium">
                                Prescription {prescription.id}
                              </span>
                            </div>
                            <Badge variant={prescription.status === 'issued' ? 'default' : 'secondary'}>
                              {prescription.status}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-gray-600">Date</p>
                              <p className="text-sm">
                                {prescription.createdAt 
                                  ? safeFormatDateOnly(prescription.createdAt) 
                                  : (prescription as any).created_at 
                                    ? safeFormatDateOnly((prescription as any).created_at)
                                    : '-'}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Medications</p>
                              <p className="text-sm">
                                {(() => {
                                  // Handle medications - could be array, string, or object
                                  let medArray: any[] = [];
                                  if (Array.isArray(prescription.medications)) {
                                    medArray = prescription.medications;
                                  } else if (typeof prescription.medications === 'string') {
                                    try {
                                      const parsed = JSON.parse(prescription.medications);
                                      medArray = Array.isArray(parsed) ? parsed : [];
                                    } catch {
                                      medArray = [];
                                    }
                                  }
                                  return medArray.length > 0 ? `${medArray.length} medication${medArray.length !== 1 ? 's' : ''}` : 'No medications';
                                })()}
                              </p>
                            </div>
                            {prescription.diagnosis && (
                              <div>
                                <p className="text-sm font-medium text-gray-600">Clinical Diagnosis</p>
                                <p className="text-sm font-medium">{prescription.diagnosis}</p>
                              </div>
                            )}
                            {prescription.chief_complaints && (
                              <div>
                                <p className="text-sm font-medium text-gray-600">Complaints</p>
                                <p className="text-sm">{prescription.chief_complaints}</p>
                              </div>
                            )}
                          </div>
                          
                          {prescription.instructions && 
                           prescription.instructions !== 'Draft prescription - instructions to be added' && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-sm font-medium text-gray-600">Instructions</p>
                              <p className="text-sm">{prescription.instructions}</p>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Bills Tab */}
            <TabsContent value="bills" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Billing History</CardTitle>
                </CardHeader>
                <CardContent>
                  {patientBills.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No bills found.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[120px] text-center">Bill Number</TableHead>
                          <TableHead className="w-[120px] text-center">Date</TableHead>
                          <TableHead className="w-[120px] text-center">Total Amount</TableHead>
                          <TableHead className="w-[120px] text-center">Amount Paid</TableHead>
                          <TableHead className="w-[100px] text-center">Services</TableHead>
                          <TableHead className="w-[100px] text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {patientBills.map((bill) => (
                          <TableRow key={bill.id}>
                            <TableCell className="text-center whitespace-nowrap">
                              <div className="flex items-center justify-center gap-2">
                                <Receipt className="h-4 w-4 text-gray-500" />
                                <span className="font-medium text-sm">
                                  {bill.billNumber}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center whitespace-nowrap">
                              <span className="text-sm">
                                {safeFormatDateOnly(bill.billDate)}
                              </span>
                            </TableCell>
                            <TableCell className="text-center whitespace-nowrap">
                              <span className="font-medium text-sm">
                                ₹{bill.totalAmount.toFixed(2)}
                              </span>
                            </TableCell>
                            <TableCell className="text-center whitespace-nowrap">
                              <span className="font-medium text-sm">
                                ₹{bill.amountPaid.toFixed(2)}
                              </span>
                            </TableCell>
                            <TableCell className="text-center whitespace-nowrap">
                              <span className="text-sm">
                                {bill.services.length} service(s)
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant={bill.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                                {bill.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
} 